/**
 * ⭐ 2026-10-07 用户需求：「**URL 携带站点**」（可分享、可复现）。
 *
 * ## 单一真相源（Lead 批准的设计）
 * **URL = 真相，store = 派生镜像**，且**只有一个方向写**：
 * ```text
 *   选择器点击 ──写 URL──▶ URL ──镜像──▶ store ──▶ 页面读 shopId
 *                            ▲
 *                      popstate（前进/后退）也走同一条镜像路径
 * ```
 * ⇒ **"两个 effect 互相写"在结构上不成立**（本会话「花费总览切店铺卡死」的根因就是双向 effect 互写）。
 *
 * ## 与全局默认值规则的分工（**不要合并**）
 *  · 本模块 / store / 选择器：**不猜店铺** —— URL 与 localStorage 都没有，就是未选（`""`）；
 *  · 「只可见一个店铺 ⇒ 自动选中」**只**发生在**四个站点必选页面**的
 *    `shopRequired.resolveShopRequirement` 判定里（写 URL 一次、幂等）。
 *  · `globalContext.resolveShopDefault`（无效 ⇒ 回落「全部店铺」）语义**不变**，服务于其它页面。
 */

/** 站点在 URL 里的参数名。 */
export const SHOP_URL_PARAM = "shopId";

/**
 * 从 `location.search` 里读站点（纯函数）。
 *  · 缺参 / 空值 / 非法 ⇒ `null`（**未选**，不是"全部"以外的第三种语义）；
 *  · 多值（`?shopId=a&shopId=b`）⇒ 取**第一个**非空值（与 URLSearchParams.get 一致）。
 */
export function shopIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get(SHOP_URL_PARAM);
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * 把站点写进 `search`（纯函数，**幂等**）：
 *  · `shopId === ""` ⇒ **移除**该参数（"全部站点/未选"不写进 URL）；
 *  · 其它参数**原样保留**（顺序稳定：先原有参数，再追加 `shopId`）⇒ 重复调用结果相同。
 */
export function searchWithShopId(search: string, shopId: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const next = shopId.trim();
  if (next === "") params.delete(SHOP_URL_PARAM);
  else params.set(SHOP_URL_PARAM, next);
  const rendered = params.toString();
  return rendered === "" ? "" : `?${rendered}`;
}

/**
 * 站点初始值（纯函数，**URL 优先**）：
 *  1. URL 里有 `shopId` ⇒ 用它（**即使 localStorage 里是别的店** —— URL 是真相）；
 *  2. URL 没有 ⇒ 回落 localStorage（**老链接兼容**）；
 *  3. 都没有 ⇒ `""`（未选）。
 */
export function initialShopId(input: { readonly search: string; readonly stored: string | null }): string {
  const fromUrl = shopIdFromSearch(input.search);
  if (fromUrl !== null) return fromUrl;
  const stored = input.stored === null ? "" : input.stored.trim();
  return stored;
}
